import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getNearbyAmbulanceProviders, createAmbulanceTripAssignmentApi } from "../../http";
import { ShowAlert } from "../../utils/showAlert";
import { AmbulanceProviderCard, AmbulanceProviderData } from "../../components/molecules/ambulanceProviderCard/AmbulanceProviderCard";
import style from "../ambulanceProviders/ambulanceProviders.module.scss";

export const AmbulanceTripSelectProvidersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const trip          = (location.state as any)?.trip;              // ambulancetrips doc
  const preAssignedIds: string[] = (location.state as any)?.assignedProviderIds ?? [];

  const [providers, setProviders]     = useState<AmbulanceProviderData[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [addedIds, setAddedIds]       = useState<Set<string>>(new Set(preAssignedIds));
  const [shortlisted, setShortlisted] = useState<AmbulanceProviderData[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!trip) { setError("Trip context missing."); setLoading(false); return; }

    const coords = trip.pickupLocation?.coordinates;
    if (!coords?.[0] || !coords?.[1]) {
      setError("Pickup coordinates not available for this trip.");
      setLoading(false);
      return;
    }

    getNearbyAmbulanceProviders({ latitude: coords[0], longitude: coords[1] })
      .then((res) => setProviders(res.data?.data ?? []))
      .catch(() => setError("Failed to fetch nearby providers. Please try again."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShortlist = (item: AmbulanceProviderData) => {
    setShortlisted((prev) =>
      prev.some((p) => p._id === item._id)
        ? prev.filter((p) => p._id !== item._id)
        : [...prev, item],
    );
  };

  const handleSelectOverride = async (item: AmbulanceProviderData) => {
    const { provider } = item;
    if (addedIds.has(provider._id)) return;
    try {
      // Trip already exists — just create the assignment
      await createAmbulanceTripAssignmentApi(trip._id, {
        providerId:  provider._id,
        providerName: provider.operatorName,
      });
      ShowAlert("success", `${provider.operatorName} added successfully`);
      setAddedIds((prev) => new Set([...prev, provider._id]));
    } catch {
      ShowAlert("error", "Failed to add provider");
    }
  };

  return (
    <div className={style.page}>
      <div className={style.header}>
        <div>
          <h2 className={style.title}>Nearby Ambulance Providers</h2>
          {!loading && !error && (
            <p className={style.subtitle}>
              {providers.length === 0
                ? "No ambulance providers found near the pickup location."
                : <>We can see <strong>{providers.length}</strong> ambulance provider{providers.length !== 1 ? "s" : ""} near the{" "}
                    <span className={style.highlight}>pickup location</span></>
              }
            </p>
          )}
          {loading     && <p className={style.subtitle}>Loading providers...</p>}
          {!loading && error && <p className={style.subtitle}>{error}</p>}
          {trip && (
            <p className={style.subtitle} style={{ marginTop: 4 }}>
              Ticket: <strong>{trip.ticketId}</strong>
            </p>
          )}
        </div>
        <button className={style.back_btn} onClick={() => navigate(-1)}>Back</button>
      </div>

      {!loading && !error && providers.length > 0 && (
        <div className={style.grid}>
          {providers.map((item) => (
            <AmbulanceProviderCard
              key={item._id}
              data={item}
              isShortlisted={shortlisted.some((p) => p._id === item._id)}
              isSelected={addedIds.has(item.provider._id)}
              onShortlist={handleShortlist}
              shortlistedProviders={shortlisted}
              onSelectOverride={handleSelectOverride}
              selectBtnLabel={addedIds.has(item.provider._id) ? "Added ✓" : "Add Provider"}
              hideShortlist
            />
          ))}
        </div>
      )}
    </div>
  );
};
